import {
  addDoc,
  collection,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  CATEGORY_PRIORITY_MAP,
  CreateReportRequest,
  Report,
  ReportStatus
} from '../types/reporting';
import { db, storage } from '../utils/firebaseConfig';
import { eventService } from './eventService';
import { notificationService } from './notificationService';
import { userService } from './userService';

class ReportService {
  private readonly REPORTS_COLLECTION = 'reports';
  private readonly REPORT_ACTIONS_COLLECTION = 'report-actions';
  private readonly USER_REPORTS_COLLECTION = 'user-reports';
  private readonly EVENT_REPORTS_COLLECTION = 'event-reports';
  private readonly EVIDENCE_STORAGE_PATH = 'report-evidence';

  async submitReport(reportData: CreateReportRequest, reporterId: string): Promise<Report> {
    try {
      // Check for duplicate reports from the same user
      const existingReport = await this.checkDuplicateReport(
        reporterId, 
        reportData.targetId, 
        reportData.type
      );

      if (existingReport) {
        throw new Error('You have already reported this event');
      }

      // Upload evidence if provided
      const evidenceUrls: string[] = [];
      if (reportData.evidence && reportData.evidence.length > 0) {
        for (const file of reportData.evidence) {
          const url = await this.uploadEvidence(file, reporterId);
          evidenceUrls.push(url);
        }
      }

      // Get target information for context
      const targetInfo = await this.getTargetInfo(reportData.targetId, reportData.type);
      const reporterInfo = await userService.getUserProfile(reporterId);

      // Determine priority based on category
      const priority = CATEGORY_PRIORITY_MAP[reportData.category];

      // Create report document
      const reportDoc: Omit<Report, 'id'> = {
        type: reportData.type,
        targetId: reportData.targetId,
        reporterId,
        category: reportData.category,
        reason: reportData.reason,
        evidence: evidenceUrls,
        status: 'pending',
        priority,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        
        // Context information
        targetTitle: targetInfo?.title || 'Unknown',
        targetType: reportData.type,
        reporterName: reporterInfo?.displayName || 'Anonymous'
      };

      // Only add description if it exists
      if (reportData.description) {
        reportDoc.description = reportData.description;
      }

      // Add to main reports collection
      const reportRef = await addDoc(collection(db, this.REPORTS_COLLECTION), reportDoc);
      
      // Create the full report object
      const report: Report = {
        id: reportRef.id,
        ...reportDoc
      };

      // Add to target-specific subcollections for easy lookup
      await this.addToTargetReports(report);

      // Send notifications for high priority reports
      if (priority === 'high' || priority === 'urgent') {
        await this.sendModerationAlert(report);
      }

      return report;
    } catch (error) {
      console.error('Error submitting report:', error);
      if (error instanceof Error && error.message === 'You have already reported this event') {
        throw error;
      }
      throw new Error('Failed to submit report');
    }
  }

  private async checkDuplicateReport(
    reporterId: string, 
    targetId: string, 
    type: string
  ): Promise<boolean> {
    try {
      // Simplified query to avoid complex index requirements
      const recentReportsQuery = query(
        collection(db, this.REPORTS_COLLECTION),
        where('reporterId', '==', reporterId),
        where('targetId', '==', targetId),
        where('type', '==', type),
        limit(1)
      );

      const snapshot = await getDocs(recentReportsQuery);
      
      // Check if any report was created in the last 24 hours
      if (!snapshot.empty) {
        const reportDoc = snapshot.docs[0];
        const reportData = reportDoc.data();
        const createdAt = reportData.createdAt.toDate();
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        
        return createdAt > twentyFourHoursAgo;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking duplicate report:', error);
      return false;
    }
  }

  private async uploadEvidence(file: File, reporterId: string): Promise<string> {
    try {
      const timestamp = Date.now();
      const fileName = `${reporterId}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${this.EVIDENCE_STORAGE_PATH}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Error uploading evidence:', error);
      throw new Error('Failed to upload evidence');
    }
  }

  private async getTargetInfo(targetId: string, type: string): Promise<{ title: string } | null> {
    try {
      if (type === 'event') {
        const event = await eventService.fetchEventById(targetId);
        return event ? { title: event.title } : null;
      } else if (type === 'user') {
        const user = await userService.getUserProfile(targetId);
        return user ? { title: user.displayName } : null;
      }
      return null;
    } catch (error) {
      console.error('Error getting target info:', error);
      return null;
    }
  }

  private async addToTargetReports(report: Report): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      if (report.type === 'event') {
        const eventReportRef = doc(
          db, 
          this.EVENT_REPORTS_COLLECTION, 
          report.targetId, 
          'reports', 
          report.id
        );
        batch.set(eventReportRef, {
          reportId: report.id,
          category: report.category,
          status: report.status,
          priority: report.priority,
          createdAt: report.createdAt
        });
      } else if (report.type === 'user') {
        const userReportRef = doc(
          db, 
          this.USER_REPORTS_COLLECTION, 
          report.targetId, 
          'reports', 
          report.id
        );
        batch.set(userReportRef, {
          reportId: report.id,
          category: report.category,
          status: report.status,
          priority: report.priority,
          createdAt: report.createdAt
        });
      }

      await batch.commit();
    } catch (error) {
      console.error('Error adding to target reports:', error);
      // Don't throw - this is not critical for the main flow
    }
  }

  private async sendModerationAlert(report: Report): Promise<void> {
    try {
      await notificationService.sendModerationAlert(report);
    } catch (error) {
      console.error('Error sending moderation alert:', error);
    }
  }

  // Moderation methods
  async getReportsQueue(
    status?: ReportStatus, 
    priority?: string,
    pageSize: number = 20,
    lastDoc?: DocumentSnapshot
  ): Promise<{ reports: Report[], lastDoc: DocumentSnapshot | null, hasMore: boolean }> {
    try {
      let reportsQuery = query(
        collection(db, this.REPORTS_COLLECTION),
        orderBy('priority', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      if (status) {
        reportsQuery = query(reportsQuery, where('status', '==', status));
      }

      if (priority) {
        reportsQuery = query(reportsQuery, where('priority', '==', priority));
      }

      if (lastDoc) {
        reportsQuery = query(reportsQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(reportsQuery);
      const reports = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Report[];

      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      const hasMore = snapshot.docs.length === pageSize;

      return { reports, lastDoc: newLastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching reports queue:', error);
      throw new Error('Failed to fetch reports');
    }
  }

  async updateReportStatus(reportId: string, status: ReportStatus, moderatorId?: string): Promise<void> {
    try {
      const reportRef = doc(db, this.REPORTS_COLLECTION, reportId);
      const updateData: any = {
        status,
        updatedAt: Timestamp.now()
      };

      if (moderatorId) {
        updateData.assignedTo = moderatorId;
      }

      if (status === 'resolved' || status === 'dismissed') {
        updateData.resolvedAt = Timestamp.now();
      }

      await updateDoc(reportRef, updateData);
    } catch (error) {
      console.error('Error updating report status:', error);
      throw new Error('Failed to update report status');
    }
  }

  async addModeratorNotes(reportId: string, notes: string, moderatorId: string): Promise<void> {
    try {
      const reportRef = doc(db, this.REPORTS_COLLECTION, reportId);
      await updateDoc(reportRef, {
        moderatorNotes: notes,
        assignedTo: moderatorId,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding moderator notes:', error);
      throw new Error('Failed to add notes');
    }
  }

  async getReportById(reportId: string): Promise<Report | null> {
    try {
      const reportRef = doc(db, this.REPORTS_COLLECTION, reportId);
      const reportSnap = await getDoc(reportRef);

      if (reportSnap.exists()) {
        return {
          id: reportSnap.id,
          ...reportSnap.data()
        } as Report;
      }

      return null;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw new Error('Failed to fetch report');
    }
  }

  async getTargetReports(targetId: string, targetType: 'event' | 'user'): Promise<Report[]> {
    try {
      const reportsQuery = query(
        collection(db, this.REPORTS_COLLECTION),
        where('targetId', '==', targetId),
        where('type', '==', targetType),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(reportsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Report[];
    } catch (error) {
      console.error('Error fetching target reports:', error);
      throw new Error('Failed to fetch reports for target');
    }
  }
}

export const reportService = new ReportService();